import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { TrendingUp } from "lucide-react";
import { Logo } from "@/components/Logo";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export function AuthShell({ title, subtitle, children }) {
  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/campus";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center px-4 grain">
      <div
        className="absolute inset-0 opacity-20"
        style={{ background: "radial-gradient(ellipse 50% 40% at 50% 0%, rgba(45,212,191,0.2), transparent 70%)" }}
      />
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center justify-center mb-8">
          <Logo size={40} />
        </Link>
        <div className="rounded-2xl bg-[#121721] border border-white/10 p-8 space-y-6">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white">{title}</h1>
            <p className="text-sm text-slate-400 mt-1">{subtitle}</p>
          </div>
          {children}
          <div className="flex items-center gap-3">
            <div className="h-px bg-white/10 flex-1" />
            <span className="text-xs text-slate-500">or</span>
            <div className="h-px bg-white/10 flex-1" />
          </div>
          <Button
            data-testid="google-login-btn"
            onClick={startGoogle}
            variant="outline"
            className="w-full h-11 rounded-full border-white/15 bg-white/5 text-white hover:bg-white/10 font-medium"
          >
            <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="" className="h-4 w-4 mr-2" />
            Continue with Google
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      toast.success("Welcome back!");
      navigate("/campus");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Sign in" subtitle="Back to Campus.">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-2">
          <Label className="text-slate-300">Email</Label>
          <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-[#0B0E14] border-white/10 text-white focus-visible:ring-amber-500" />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">Password</Label>
          <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="bg-[#0B0E14] border-white/10 text-white focus-visible:ring-amber-500" />
        </div>
        <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold h-11 rounded-full">
          {loading ? "Signing in…" : "Sign in"}
        </Button>
      </form>
      <p className="text-center text-sm">
        <Link to="/forgot-password" data-testid="link-forgot-password" className="text-slate-400 hover:text-amber-300">Forgot password?</Link>
      </p>
      <p className="text-center text-sm text-slate-400">
        New here?{" "}
        <Link to="/register" data-testid="link-register" className="text-amber-400 hover:text-amber-300 font-medium">
          Create an account
        </Link>
      </p>
    </AuthShell>
  );
}
